import { useQuery } from '@zg-rentals/react-query5';
import {
  type AreaDisplayEncodedGeometryDto,
  MarketAnalyticsWebApiHandler_getAreaDisplayGeometry,
  MarketAnalyticsWebApiHandler_getAreaDisplayGeometry_Path,
  type PortfolioAnalyticsPropertyDto,
  type PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import type { GeoJson } from '../../SharedComponents/Map/GeoJsonLayer';
import type { MainLayoutType } from './MainLayoutContext';
import { type MainLayoutContextValue, type MapBounds, type RegionIdCenterMapping } from './MainLayoutContext';
import { encodedPolygonToGeoJSONFeature } from '../../../utils/mapUtils/encodedPolygonToGeoJSONFeature';
import { isPointVisibleInBound } from '../../../utils/mapUtils/isPointVisibleInBound';
import { useMemo } from 'react';
import { DisplayingRegionLevel } from '../../../types/DisplayingRegionLevel';
import { getGeoJSONBounds } from '../../../utils/mapUtils/getGeoJSONBounds';

const getRegionIdKey = (
  displayMode: DisplayingRegionLevel,
  property: PortfolioAnalyticsPropertyResponseDto,
): { key: keyof PortfolioAnalyticsPropertyDto; type: DisplayingRegionLevel } => {
  switch (displayMode) {
    case DisplayingRegionLevel.COUNTRY:
      return { key: 'stateRegionId', type: DisplayingRegionLevel.STATE };
    case DisplayingRegionLevel.STATE:
      return { key: 'stateRegionId', type: DisplayingRegionLevel.STATE };
    case DisplayingRegionLevel.METRO_AREA:
      if (!property.propertyDetails.metroStatRegionId) return { key: 'cityRegionId', type: DisplayingRegionLevel.CITY };
      return { key: 'metroStatRegionId', type: DisplayingRegionLevel.METRO_AREA };
    case DisplayingRegionLevel.CITY:
      return { key: 'cityRegionId', type: DisplayingRegionLevel.CITY };
    default:
      return { key: 'zipRegionId', type: DisplayingRegionLevel.ZIP };
  }
};

const getAreaDisplayGeometryCache = new Map<number, AreaDisplayEncodedGeometryDto>();

export const useGeojsonToDisplay = (
  selectedRegionId: number,
  selectedDisplayingRegionLevel: DisplayingRegionLevel,
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>,
  displayMode: DisplayingRegionLevel,
  bounds: MapBounds,
  setBounds: (bounds: MapBounds) => void,
  regionIdCenterMapping: RegionIdCenterMapping | null,
  layout: MainLayoutType,
  shouldForceBoundsToGeojsonBounds: boolean,
  setShouldForceBoundsToGeojsonBounds: (shouldForceBoundsToGeojsonBounds: boolean) => void,
  selectedFilters: MainLayoutContextValue['selectedFilters'],
) => {
  const regionIds: Array<{
    regionId: number;
    regionIdType: DisplayingRegionLevel;
    property: PortfolioAnalyticsPropertyResponseDto;
  }> = useMemo(
    () => [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      displayMode,
      bounds.northLat,
      bounds.southLat,
      bounds.eastLng,
      bounds.westLng,
      selectedRegionId,
      selectedDisplayingRegionLevel,
      selectedFilters.listingAlias,
    ],
  );

  const selectedProperty = filteredProperties.find(
    (p) => p.propertyDetails.listingAlias === selectedFilters.listingAlias,
  );

  const selectedRegionIdToUse = selectedProperty
    ? selectedProperty?.propertyDetails.metroStatRegionId || selectedProperty?.propertyDetails.cityRegionId
    : selectedRegionId;

  if (!selectedRegionIdToUse) {
    for (const property of filteredProperties) {
      const { key: regionIdKey, type: regionIdType } = getRegionIdKey(displayMode, property);
      const regionId = property.propertyDetails[regionIdKey] as number;
      const regionCenter = regionIdCenterMapping?.get(regionId)?.center;

      if (regionId && regionCenter) {
        const isRegionCenterVisible = isPointVisibleInBound(regionCenter, bounds);
        if (isRegionCenterVisible && !regionIds.some((r) => r.regionId === regionId)) {
          regionIds.push({ regionId: Number(regionId), regionIdType, property });
        }
      }
    }
  } else if (!regionIds.some((r) => r.regionId === Number(selectedRegionIdToUse))) {
    if (selectedFilters.listingAlias !== '' && selectedProperty) {
      const metroStatRegionId = selectedProperty.propertyDetails.metroStatRegionId;
      const cityRegionId = selectedProperty.propertyDetails.cityRegionId;

      if (metroStatRegionId) {
        regionIds.push({
          regionId: Number(metroStatRegionId),
          regionIdType: DisplayingRegionLevel.METRO_AREA,
          property: selectedProperty,
        });
      } else {
        regionIds.push({
          regionId: Number(cityRegionId),
          regionIdType: DisplayingRegionLevel.CITY,
          property: selectedProperty,
        });
      }
    } else {
      const property = filteredProperties.find(
        (p) =>
          p.propertyDetails.stateRegionId === selectedRegionId ||
          p.propertyDetails.metroStatRegionId === selectedRegionId ||
          p.propertyDetails.cityRegionId === selectedRegionId ||
          p.propertyDetails.zipRegionId === selectedRegionId,
      );
      if (property) {
        regionIds.push({
          regionId: Number(selectedRegionIdToUse),
          regionIdType: selectedDisplayingRegionLevel,
          property,
        });
      }
    }
  }

  const {
    data: geojson,
    isLoading,
    error,
  } = useQuery({
    queryKey: [MarketAnalyticsWebApiHandler_getAreaDisplayGeometry_Path, { regionIds }],
    queryFn: async () => {
      const promises = [];

      for (const { regionId, regionIdType, property } of regionIds) {
        const cacheResponse = getAreaDisplayGeometryCache.get(regionId);

        if (cacheResponse) {
          promises.push(
            Promise.resolve({ response: cacheResponse, regionId, regionIdType, fromCache: true, property }),
          );
        } else {
          const promise = MarketAnalyticsWebApiHandler_getAreaDisplayGeometry({ zillowRegionId: regionId }).then(
            (response) => ({
              regionId,
              regionIdType,
              property,
              response,
              fromCache: false,
            }),
          );
          promises.push(promise);
        }
      }
      const responses = await Promise.all(promises);

      const polygons: Array<GeoJSON.Feature> = [];

      responses.forEach(({ response, fromCache, regionId, regionIdType, property }) => {
        if (!fromCache) {
          getAreaDisplayGeometryCache.set(regionId, response);
        }
        const currentPolygons = response.encodedPolygons.map((p) => ({
          ...encodedPolygonToGeoJSONFeature(p),
          properties: { regionId: Number(regionId), regionIdType, property },
        }));
        polygons.push(...currentPolygons);
      });

      const geojson: GeoJson = {
        type: 'FeatureCollection',
        features: polygons,
      };

      return geojson;
    },
    enabled: regionIdCenterMapping !== null,
  });

  if (shouldForceBoundsToGeojsonBounds && geojson && geojson.features && geojson.features.length > 0) {
    if (selectedFilters.listingAlias !== '') {
      const selectedProperty = filteredProperties.find(
        (p) => p.propertyDetails.listingAlias === selectedFilters.listingAlias,
      );
      if (selectedProperty) {
        const PADDING = 0.02;
        const lat = selectedProperty?.propertyDetails.lat ?? 0;
        const lon = selectedProperty?.propertyDetails.lon ?? 0;
        const newBounds = {
          eastLng: lon + PADDING,
          westLng: lon - PADDING,
          northLat: lat + PADDING,
          southLat: lat - PADDING,
        };
        setBounds(newBounds);
      }
    } else {
      const newBounds = getGeoJSONBounds(geojson);
      setBounds(newBounds);
    }
    setShouldForceBoundsToGeojsonBounds(false);
  }

  return {
    geojson: geojson as GeoJson,
    visibleRegions: regionIds,
    isLoading,
    error,
  };
};
